import { config } from "../config.js";
import { randomInt } from "./rng.js";

// 武器と強化の定義。levels[i] は レベル i+1 の性能。
// evolution: 進化(2026-10-31 オーナー要望「武器Lv5より上が無い」)。武器がLv5(最大)で、対になる強化が config.evolution.needPassiveLevel 以上のとき、
//   レベルアップの選択肢に進化が出る。選ぶと武器のレベルが 6(= levels.length + 1)になり、stats の性能になる。名前は src/i18n/ の weapon.<id>.evo.name
// 名前・説明など画面に出す文は src/i18n/(キーは weapon.<id>.name など)。組み立ては src/ui/labels.js。
// 数値を変えたら config.sim.version を上げる(古いリプレイが再現できなくなる)。

export const WEAPONS = {
  beam: {
    levels: [
      { count: 1, cooldown: 0.45, damage: 10, speed: 420, size: 1 },
      { count: 2, cooldown: 0.4, damage: 12, speed: 440, size: 1.2 },
      { count: 3, cooldown: 0.34, damage: 14, speed: 460, size: 1.4 },
      { count: 4, cooldown: 0.28, damage: 17, speed: 480, size: 1.7 },
      { count: 6, cooldown: 0.22, damage: 22, speed: 520, size: 2.1 },
    ],
    evolution: { with: "power", stats: { count: 8, cooldown: 0.17, damage: 30, speed: 580, size: 2.6 } },
  },
  spread: {
    levels: [
      { count: 8, cooldown: 1.0, damage: 7, angle: 0.22, size: 1 },
      { count: 12, cooldown: 0.9, damage: 8, angle: 0.26, size: 1.2 },
      { count: 16, cooldown: 0.8, damage: 9, angle: 0.3, size: 1.4 },
      { count: 20, cooldown: 0.7, damage: 11, angle: 0.34, size: 1.6 },
      { count: 28, cooldown: 0.55, damage: 13, angle: 0.38, size: 1.9 },
    ],
    evolution: { with: "haste", stats: { count: 36, cooldown: 0.42, damage: 17, angle: 0.42, size: 2.3 } },
  },
  missile: {
    levels: [
      { count: 1, cooldown: 1.5, damage: 22, blast: 9 },
      { count: 2, cooldown: 1.35, damage: 25, blast: 11 },
      { count: 4, cooldown: 1.2, damage: 28, blast: 13 },
      { count: 6, cooldown: 1.05, damage: 32, blast: 16 },
      { count: 9, cooldown: 0.85, damage: 40, blast: 20 },
    ],
    evolution: { with: "thruster", stats: { count: 12, cooldown: 0.65, damage: 52, blast: 26 } },
  },
  saber: {
    levels: [
      { count: 1, radius: 13, damage: 14, spin: 3.2 },
      { count: 2, radius: 15, damage: 17, spin: 3.5 },
      { count: 3, radius: 18, damage: 21, spin: 3.8 },
      { count: 4, radius: 22, damage: 26, spin: 4.1 },
      { count: 6, radius: 27, damage: 34, spin: 4.6 },
    ],
    evolution: { with: "shield", stats: { count: 8, radius: 33, damage: 46, spin: 5.2 } },
  },
  mainGun: {
    levels: [
      { cooldown: 6.0, damage: 40, radius: 7 },
      { cooldown: 5.2, damage: 55, radius: 9 },
      { cooldown: 4.4, damage: 70, radius: 11 },
      { cooldown: 3.6, damage: 90, radius: 14 },
      { cooldown: 2.8, damage: 120, radius: 18 },
    ],
    evolution: { with: "magnet", stats: { cooldown: 2.0, damage: 170, radius: 24 } },
  },
};

// 自機の能力の強化。value はレベルごとの合計の効き目
export const PASSIVES = {
  power: { levels: [1.2, 1.4, 1.6, 1.85, 2.1] },
  haste: { levels: [0.88, 0.78, 0.7, 0.62, 0.55] },
  thruster: { levels: [1.1, 1.2, 1.3, 1.4, 1.5] },
  magnet: { levels: [1.4, 1.8, 2.2, 2.7, 3.3] },
  shield: { levels: [1.2, 1.4, 1.6, 1.8, 2.0] },
};

export const HEAL_OPTION = { kind: "heal", id: "heal", amount: 30 };

// レベル lv の性能(lv が levels.length + 1 なら進化した性能)
export function statsAt(id, lv) {
  const w = WEAPONS[id];
  if (!lv) return null;
  return lv > w.levels.length ? w.evolution.stats : w.levels[lv - 1];
}

export const isEvolved = (id, lv) => lv > WEAPONS[id].levels.length;

export function weaponStats(world, id) {
  return statsAt(id, world.player.weapons[id]);
}

// 今すぐ進化できる武器(武器が最大 + 対の強化が足りている)
function evolvable(world) {
  const p = world.player;
  return Object.keys(WEAPONS).filter((id) => {
    const w = WEAPONS[id];
    return p.weapons[id] === w.levels.length && (p.passives[w.evolution.with] || 0) >= config.evolution.needPassiveLevel;
  });
}

export function passiveValue(world, id) {
  const lv = world.player.passives[id] || 0;
  return lv ? PASSIVES[id].levels[lv - 1] : 1;
}

// レベルアップの選択肢を乱数で選ぶ(同じ乱数なら同じ選択肢)。進化できる武器があれば、1つは必ず進化にする
export function rollChoices(world, n) {
  const picked = [];
  const evo = evolvable(world);
  if (evo.length) {
    const id = evo[randomInt(world, evo.length)];
    picked.push({ kind: "evolve", id, level: WEAPONS[id].levels.length + 1 });
  }
  const pool = [];
  for (const id of Object.keys(WEAPONS)) {
    const lv = world.player.weapons[id] || 0;
    if (lv < WEAPONS[id].levels.length) pool.push({ kind: "weapon", id, level: lv + 1 });
  }
  for (const id of Object.keys(PASSIVES)) {
    const lv = world.player.passives[id] || 0;
    if (lv < PASSIVES[id].levels.length) pool.push({ kind: "passive", id, level: lv + 1 });
  }
  while (picked.length < n && pool.length) picked.push(pool.splice(randomInt(world, pool.length), 1)[0]);
  if (!picked.length) picked.push({ kind: "heal", id: "heal", level: 0 });
  return picked;
}

export function applyChoice(world, choice) {
  const p = world.player;
  if (choice.kind === "weapon" || choice.kind === "evolve") {
    p.weapons[choice.id] = choice.level;
    if (!(choice.id in p.cooldowns)) p.cooldowns[choice.id] = 0;
  } else if (choice.kind === "passive") {
    p.passives[choice.id] = choice.level;
    if (choice.id === "shield") heal(world, 25, "upgrade");
  } else {
    heal(world, HEAL_OPTION.amount, "upgrade");
  }
}

// SHIELDを回復する。画面に「SHIELD +N」を出すため、出来事にも残す
export function heal(world, amount, source) {
  const p = world.player;
  const before = p.shield;
  p.shield = Math.min(maxShield(world), p.shield + amount);
  world.events.push({ k: "heal", amount: Math.round(p.shield - before), source });
}

// 攻撃力の倍率: プレイ中の出力強化 × 格納庫の出力 × R.W.N.D.の出力ブースト(画面の「攻撃力 +N%」も同じ値)
export function attackPower(world) {
  const p = world.player;
  return passiveValue(world, "power") * world.params.power * (p.powerBoost > 0 ? config.rewind.boosts.power.damage : 1);
}

export function maxShield(world) {
  return world.params.maxShield * passiveValue(world, "shield");
}
